import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Button } from 'reactstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useNavigate, Link } from 'react-router-dom';
import { Modulo } from '../../../Types/Tables.type';
import { useIteamResource } from '../../../hook/ShowTable/generic';
import FilterComponent from '../../../Components/Tables/DataTables/Common/FilterComponent';
import Loader from '../../../Components/Widgets/Loader/generic';
import { Btn } from '../../../AbstractElements';
import DeleteConfirm from '../../../Components/Modals/Delete'

type FlatModulo = Modulo & { __depth: number };

export default function Index() {
  const [filterText, setFilterText] = useState('');
  const { data: rows, loading, refetch  } = useIteamResource<Modulo[]>('/configurazione_menu/list');
  const navigate = useNavigate();


  // Mappa ID -> Nome (per la colonna "Parent")
  const idToName = useMemo(() => {
    const map = new Map<string | number, string>();
    const walk = (list: Modulo[] | undefined) => {
      if (!Array.isArray(list)) return;
      for (const m of list) {
        map.set(m.ID, getNameFromMeta((m as any).meta, 'it')); // ← qui
        if (Array.isArray(m.children) && m.children.length) walk(m.children);
      }
    };
    walk(Array.isArray(rows) ? rows : []);
    return map;
  }, [rows]);

  // Lista piatta per indentare e mostrare tutti i livelli
  const flatItems: FlatModulo[] = useMemo(
    () => flattenModules(rows ?? []),
    [rows]
  );

  // Filtro (incluso nome parent)
  const filteredItems: FlatModulo[] = useMemo(() => {
    if (!filterText) return flatItems;
    const q = filterText.toLowerCase();
    return flatItems.filter(item => {
      const nome = getNameFromMeta((item as any).meta, 'it');       // ← qui
      const parentName = getParentName(item.parentID, idToName);
      return [nome, item.ID, item.parentID ?? '', item.ordine, parentName ?? '']
        .map(v => (v ?? '').toString().toLowerCase())
        .some(s => s.includes(q));
    });
  }, [flatItems, filterText, idToName]);



  const handleDelete = (row: FlatModulo) => {
    console.log('Delete', row);
  };

  // Colonne
  const columns: TableColumn<FlatModulo>[] = useMemo(() => [
    {
      name: 'Nome',
      cell: (row) => {
        const nome = getNameFromMeta((row as any).meta, 'it');
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ display: 'inline-block', width: row.__depth * 16 }} />
            {nome}
          </div>
        );
      },
      sortable: true,
      sortFunction: (a, b) =>
        getNameFromMeta((a as any).meta, 'it').localeCompare(getNameFromMeta((b as any).meta, 'it')),
    },
    {
      name: 'Parent',
      selector: (row) => getParentName(row.parentID, idToName) ?? '-',
      sortable: true,
      sortFunction: (a, b) => {
        const pa = getParentName(a.parentID, idToName) ?? '';
        const pb = getParentName(b.parentID, idToName) ?? '';
        return pa.localeCompare(pb);
      },
      width: '300px',
    },
    {
      name: 'Ordine',
      selector: (row) => row.ordine,
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'Azioni',
      cell: (row) => {
        const slug = encodeURIComponent(String(row.ID));
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Variante 2: Link diretto (Ctrl/Cmd+Click apre in nuova scheda) */}
            <Button
              size="sm"
              color="primary"
              tag={Link as any}
              to={`/admin/moduli/${slug}`}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Modifica
            </Button>

            <DeleteConfirm
                url={`/configurazione_menu?ID=${row.ID}`}
                onSuccess={ async  () => {
                    await refetch();
                }}
                trigger={
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Elimina"
                    >
                        Elimina
                    </button>
                }
            />
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '280px',
    },
  ], [idToName, refetch]);

  return (
    <Container fluid className="pt-2">
      <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
        <Link to="/admin/moduli/nuovo">
          <Btn className="px-4" color="primary" type="button">
            NUOVO
          </Btn>
        </Link>
      </Col>
      <Col sm={12}>
        <Card>
          <CardBody>
            <FilterComponent
              onFilter={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
              filterText={filterText}
            />
            <div className="table-responsive">
              <DataTable<FlatModulo>
                data={filteredItems}
                columns={columns}
                progressPending={loading}
                progressComponent={<Loader />}
                pagination
                striped
                highlightOnHover
                className="display"
              />
            </div>
          </CardBody>
        </Card>
      </Col>
    </Container>
  );
}

/* ===== Helpers ===== */

function flattenModules(tree: unknown, depth = 0): FlatModulo[] {
  if (!Array.isArray(tree)) return [];
  const out: FlatModulo[] = [];
  for (const m of tree as Modulo[]) {
    out.push({ ...(m as any), __depth: depth });
    const children = Array.isArray((m as any).children) ? (m as any).children : [];
    out.push(...flattenModules(children, depth + 1));
  }
  return out;
}

function getParentName(parentID: Modulo['parentID'], map: Map<string | number, string>): string | null {
  if (parentID === null || parentID === undefined || parentID === 0 || parentID === '0') return null;
  return map.get(parentID) ?? null;
}

type MetaI18n = Record<string, { nome?: string }>;

function getNameFromMeta(meta: unknown, lang: string = 'it'): string {
  if (!meta) return '-';
  try {
    const obj: MetaI18n = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
    const byLang = obj?.[lang] || obj?.[lang.toLowerCase()] || obj?.[lang.toUpperCase()];
    return (byLang?.nome ?? '').toString() || '-';
  } catch {
    return '-';
  }
}
